import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import CocinaHeader from '../components/restaurante/cocina/CocinaHeader';
import PedidoCocinaCard from '../components/restaurante/cocina/PedidoCocinaCard';
import InventarioPinModal from '../components/cantina/productos/InventarioPinModal';
import useRestauranteCocina from '../hooks/restaurante/useRestauranteCocina';
import BarraOperador from '../components/restaurante/BarraOperador';
import AvisoRestaurante from '../components/restaurante/AvisoRestaurante';
import useOperadorRestaurante from '../hooks/restaurante/useOperadorRestaurante';

const TITULOS_ACCION = {
  iniciar: 'Iniciar preparación',
  listo: 'Marcar pedido como listo',
  entregar: 'Confirmar entrega del pedido',
};

export default function RestauranteCocina() {
  const cocina = useRestauranteCocina();
  const [operacionPendiente, setOperacionPendiente] =
    useState(null);
  const { operador, identificar } = useOperadorRestaurante();
  const [aviso, setAviso] = useState('');

  const pendientes = cocina.pedidos.filter(
    (pedido) => pedido.estado === 'CONFIRMADO'
  ).length;

  const enPreparacion = cocina.pedidos.filter(
    (pedido) => pedido.estado === 'EN_PREPARACION'
  ).length;

  const listos = cocina.pedidos.filter(
    (pedido) => pedido.estado === 'LISTO'
  ).length;

  // Con un operador identificado la acción se hace directo, sin PIN.
  const solicitarAccion = async (pedidoId, accion) => {
    if (!operador) {
      setOperacionPendiente({
        pedidoId,
        accion,
      });
      return;
    }

    try {
      await ejecutarAccion({ pedidoId, accion }, operador);
    } catch (error) {
      setAviso(
        error?.message || 'No se pudo actualizar el pedido.'
      );
    }
  };

  const confirmarConUsuario = async (usuario) => {
    if (!operacionPendiente || !usuario?.id) return;

    identificar(usuario);

    await ejecutarAccion(operacionPendiente, usuario);
    setOperacionPendiente(null);
  };

  const ejecutarAccion = async (operacion, usuario) => {
    const { pedidoId, accion } = operacion;

    let resultado;

    if (accion === 'iniciar') {
      resultado = await cocina.iniciarPreparacion(
        pedidoId,
        usuario.id
      );
    }

    if (accion === 'listo') {
      resultado = await cocina.marcarListo(
        pedidoId,
        usuario.id
      );
    }

    if (accion === 'entregar') {
      resultado = await cocina.marcarEntregado(
        pedidoId,
        usuario.id
      );
    }

    if (!resultado?.ok) {
      throw new Error(
        resultado?.error ||
          'No se pudo actualizar el pedido.'
      );
    }

  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <AvisoRestaurante
        mensaje={aviso}
        tipo="error"
        onCerrar={() => setAviso('')}
      />

      <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-4 flex justify-end">
          <BarraOperador
            operador={operador}
            onIdentificar={identificar}
          />
        </div>

        <CocinaHeader
          pendientes={pendientes}
          enPreparacion={enPreparacion}
          listos={listos}
          onRefresh={cocina.recargar}
          cargando={cocina.cargando}
        />

        {cocina.error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5">
            <p className="font-semibold text-red-700">
              {cocina.error}
            </p>
          </div>
        )}

        {cocina.cargando && cocina.pedidos.length === 0 && (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-10 text-center text-gray-500">
            Cargando pedidos de cocina...
          </div>
        )}

        {!cocina.cargando &&
          cocina.pedidos.length === 0 && (
            <div className="mt-6 rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
              <div className="text-4xl">👨‍🍳</div>

              <h2 className="mt-3 text-xl font-extrabold text-azul-oscuro">
                Cocina al día
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                No hay pedidos pendientes de preparación.
              </p>
            </div>
          )}

        {cocina.pedidos.length > 0 && (
          <section className="mt-6">
            <div className="mb-4 flex items-end justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-verde-oscuro">
                  Comanda
                </p>

                <h2 className="text-2xl font-extrabold text-azul-oscuro">
                  Pedidos
                </h2>
              </div>

              <span className="text-sm font-semibold text-gray-500">
                Actualización automática
              </span>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3">
              {cocina.pedidos.map((pedido) => (
                <PedidoCocinaCard
                  key={pedido.id}
                  pedido={pedido}
                  accionando={cocina.accionando}
                  onIniciar={(id) =>
                    solicitarAccion(id, 'iniciar')
                  }
                  onListo={(id) =>
                    solicitarAccion(id, 'listo')
                  }
                  onEntregar={(id) =>
                    solicitarAccion(id, 'entregar')
                  }
                />
              ))}
            </div>
          </section>
        )}
      </main>

      <InventarioPinModal
        abierto={Boolean(operacionPendiente)}
        titulo={
          operacionPendiente
            ? `${TITULOS_ACCION[operacionPendiente.accion]} · Pedido #${operacionPendiente.pedidoId}`
            : 'Confirmar operación'
        }
        procesando={Boolean(cocina.accionando)}
        onClose={() => setOperacionPendiente(null)}
        onConfirm={confirmarConUsuario}
      />
    </div>
  );
}
